import { useAuth } from '@/context/AuthContext';
import { ROLES } from '@/constants/roles';
import { setAllRoutes } from '@/hooks/useCurrentRoute';
import { MenuItem, TenantResDto } from '@/types';
import { getStoredLoginPayload, getStoredMenus, getStoredRole } from '@/utils/auth';
import { Local } from '@/utils/storage';
import React, { lazy, Suspense, useEffect, useState } from 'react';
import KeepAlive, { useAliveController } from 'react-activation';
import { Navigate, useLocation, useNavigate, useRoutes } from 'react-router-dom';
import { cacheManager } from './cacheManager';
import Loading from './loading';
import {
  keepAlives,
  nestedPatterns,
  protocolList,
  RouteAuthorityKey,
  showBreadcrumbs,
  showEnterpriseSearchs,
  staticDetailRoute,
} from './staticRoute';
import nyRoutes from './nyRoutes';
import { searchRoute } from './resource';
import { fetchCapitalQueryPermissions } from '@/api/Search/capitalPermissions';

const pageModules = import.meta.glob('../views/**/pages/*/index.tsx', {
  eager: false, // 改为false 实现懒加载
  import: 'default',
});
// 创建组件映射表
const componentMap: Record<string, React.LazyExoticComponent<React.ComponentType<any>>> = {};

// 动态填充组件映射表
Object.entries(pageModules).forEach(([path, module]) => {
  const normalizedPath = path
    .replace('../views/', '../views/')
    .replace(/\/pages\//, '/pages/')
    .replace(/\/index\.tsx$/, '')
    .replace(/\.tsx$/, '');

  // 修改为正确的懒加载实现
  componentMap[normalizedPath] = lazy(() =>
    module().then((mod) => ({
      default: mod as React.ComponentType<ReturnType<typeof module>>,
    })),
  );
});

const componentAliases: Record<string, string> = {
  ApprovalConfigPage: '../views/Ny/pages/ApprovalConfig',
  CustomerPage: '../views/Ny/pages/Customers',
  DispatchRulePage: '../views/Ny/pages/NyDispatch',
  NYEqxSharePage: '../views/Ny/pages/EqxManage',
  LeadPoolPage: '../views/Ny/pages/Leadpool',
  NyAllLeadsPage: '../views/Ny/pages/NYAllLeads',
  NYServiceDispatchPage: '../views/Ny/pages/ServiceDispatch',
  NyDispatchRulePage: '../views/Ny/pages/NyDispatch',
  NYRulesPage: '../views/Ny/pages/NYRules',
  NYSlaMonitorPage: '../views/Ny/pages/NYSlaMonitor',
  ParkPage: '../views/Ny/pages/Parks',
  QuestionnairePage: '../views/Ny/pages/Questionnaires',
  QuestionnairesPage: '../views/Ny/pages/Questionnaires',
  QueryToolHubPage: '../views/Search/pages/Index',
  RatingMonitorPage: '../views/Ny/pages/Ratings',
  RuleConfigPage: '../views/Ny/pages/NYRules',
  ServiceOrderPage: '../views/Ny/pages/NYAllLeads',
  ServiceOrderRulePage: '../views/Ny/pages/NYRules',
  ServiceProviderPage: '../views/Ny/pages/SpManageModule',
  Settlement: '../views/Ny/pages/Settlement',
  SlaMonitorPage: '../views/Ny/pages/NYSlaMonitor',
  TenantMemberPage: '../views/Ny/pages/NyStaff',
  TenantRolePage: '../views/Ny/pages/Roles',
};

Object.entries(componentAliases).forEach(([alias, target]) => {
  if (componentMap[target]) {
    componentMap[alias] = componentMap[target];
  }
});

const PlaceholderPage = React.lazy(() => import('@/views/Placeholder/pages'));
const Layout = lazy(() => import('../views/Layout'));
const Login = lazy(() => import('../views/Login/pages/Index'));
const AuthLoading = lazy(() => import('../views/AuthLoading'));
const DEFAULT_ROLE = 'sp-emp';

export const AuthGuard = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, logout } = useAuth();
  const location = useLocation();
  if (
    location.pathname === '/login' ||
    protocolList.includes(location.pathname) ||
    location.pathname === '/authLoading'
  ) {
    // 如果是登录页/协议页，直接显示，不检查认证状态
    return <>{children}</>;
  }
  // 检查 localStorage 作为备用
  const tokenFromStorage = Local.get('ny_pc_token');
  const shouldBeAuthenticated = isAuthenticated && tokenFromStorage;

  if (!shouldBeAuthenticated && location.pathname !== '/login') {
    // 确保状态同步
    if (isAuthenticated && !tokenFromStorage) {
      // 状态不一致，强制登出
      logout();
    }
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// 将配置转换为路由对象
function createRoutes(configs: RouteConfig[]): any {
  return configs.map((config) => {
    const { component, children, redirect, path, meta, ...rest } = config;
    let element: React.ReactNode | null = null;

    // 处理重定向
    if (redirect) {
      element = <Navigate to={redirect} replace />;
    }
    // 处理组件
    else if (component) {
      // 创建包装组件来传递 props
      const ComponentWrapper = () => {
        const location = useLocation();
        const name = location.pathname;
        const searchParams = new URLSearchParams(location.search);
        const fromCache = searchParams.get('fromCache') === 'true';
        const [key, setKey] = useState(cacheManager.get(name) ?? 0);
        const { drop } = useAliveController(); // 获取 drop 方法

        const componentElement = React.createElement(component, {
          routeMeta: meta,
          location,
        });

        if (meta?.keepAlive) {
          // eslint-disable-next-line react-hooks/rules-of-hooks
          useEffect(() => {
            const isFirstAccess = cacheManager.isFirstAccess(name);
            const when = isFirstAccess || fromCache;
            if (isFirstAccess) {
              cacheManager.set(name, key);
            } else {
              // 非首次加载，不更新key
              setKey(cacheManager.get(name)!);
            }
            if (!when) {
              drop(name); // 清除指定 name 的缓存
              setKey((prev) => {
                const value = prev + 1;
                cacheManager.set(name, value);
                return value;
              });
            }
          }, [name, drop]);
          return (
            <KeepAlive
              key={key} // 添加 key 强制重新创建
              name={name}
              id={name}
              saveScrollPosition
              when
            >
              {componentElement}
            </KeepAlive>
          );
        }
        return componentElement;
      };
      const ComponentElement = (
        <Suspense fallback={<Loading />}>
          <ComponentWrapper />
        </Suspense>
      );

      // 登录页不需要 AuthGuard
      if (path === '/login') {
        element = ComponentElement;
      } else {
        element = <AuthGuard>{ComponentElement}</AuthGuard>;
      }
    }

    return {
      ...rest,
      path,
      element,
      meta,
      children: children ? createRoutes(children) : undefined,
    };
  });
}

/**
 * 递归转换路由配置
 *
 * @param {Array} routes - 原始路由配置数组
 * @param {string} [parentPath=""] - 父级路径，用于构建完整路径
 *
 * @description
 * 1. 遍历路由配置数组，为每个路由项构建完整路径
 * 2. 转换路由配置格式，添加必要的字段(label/key/meta等)
 * 3. 处理组件映射关系
 * 4. 递归处理子路由
 * 5. 将没有子路由的配置项添加到全局路由数组(allRoutes)中
 */
const filterStaticRouter = (routes: any[], parentPath = ''): any[] => {
  const list: any = [];
  routes.forEach((route) => {
    const fullPath = parentPath + route.path;
    const transformed: RouteConfig = {
      ...route,
      path: fullPath,
      label: route.name,
      key: fullPath,
      buttons: route.buttons,
      meta: { title: route.name, ...route.meta },
    };

    // 转换组件
    if (route.element && componentMap[route.element]) {
      transformed.component = componentMap[route.element];
    }
    if (route.children) {
      transformed.children = filterStaticRouter(route.children, fullPath);
      list.push(transformed);
    } else {
      list.push(transformed);
    }
  });
  return list;
};

/**
 * 递归转换路由配置
 *
 * @param {Array} routes - 原始路由配置数组
 * @param {string} [parentPath=""] - 父级路径，用于构建完整路径
 *
 * @description
 * 1. 遍历路由配置数组，为每个路由项构建完整路径
 * 2. 转换路由配置格式，添加必要的字段(label/key/meta等)
 * 3. 处理组件映射关系
 * 4. 递归处理子路由
 * 5. 将没有子路由的配置项添加到全局路由数组(allRoutes)中
 */
export const filterDynamicRouter = (routes: TenantResDto[], parentPath = ''): TenantResDto[] => {
  const list: any = [];
  routes.forEach((route) => {
    const fullPath = parentPath + route.path;
    const transformed: RouteConfig = {
      ...route,
      path: fullPath,
      label: route.name,
      key: fullPath,
      // buttons: route.buttons,
      meta: { title: route.name, ...route.meta },
    };

    if (showEnterpriseSearchs.indexOf(route.authorityKey) > -1) {
      transformed!.meta!.showEnterpriseSearch = true;
    }
    if (showBreadcrumbs.indexOf(route.authorityKey) > -1) {
      transformed!.meta!.showBreadcrumb = true;
    }
    if (keepAlives.indexOf(route.authorityKey) > -1) {
      transformed!.meta!.keepAlive = true;
    }
    // 转换组件
    if (route.component && componentMap[route.component]) {
      transformed.component = componentMap[route.component];
    }
    if (route.children) {
      transformed.children = filterDynamicRouter(route.children, fullPath);
      list.push(transformed);
    } else {
      list.push(transformed);
    }
  });
  return list;
};

/**
 * 多维数组
 * @param list
 */
export const multidimensionalRoute = (list: TenantResDto[]) => {
  const result: TenantResDto[] = [];
  list?.forEach((item) => {
    result.push(item);
    if (
      item.children &&
      item.children.length > 0 &&
      // 非嵌套路由
      !nestedPatterns.includes(item.authorityKey)
    ) {
      result.push(...multidimensionalRoute(item.children));
    }
    if (item.buttons && item.buttons.length > 0 && item.component) {
      result.push(...multidimensionalRoute(item.buttons));
    }
  });
  return result;
};

const singleRoutes = (list: TenantResDto[]) => {
  const routeList: TenantResDto[] = [];
  list.forEach((item) => {
    routeList.push(item);
    if (item.children) {
      routeList.push(...singleRoutes(item.children));
    }
    if (item.buttons) {
      routeList.push(...singleRoutes(item.buttons));
    }
  });
  return routeList;
};

const mergeQueryAuthorities = (
  existing: RouteAuthorityKey[],
  capitalAuthorities: string[],
): RouteAuthorityKey[] => {
  const nonQueryAuthorities = existing.filter((authority) => !String(authority).startsWith('/search'));
  return [...nonQueryAuthorities, ...(capitalAuthorities as RouteAuthorityKey[])];
};

function extractMenusFromPayload(payload: any) {
  if (!payload || typeof payload !== 'object') return [];
  const auth = payload.auth;
  if (auth && typeof auth === 'object' && Array.isArray(auth.menus)) {
    return auth.menus;
  }
  if (Array.isArray(payload.menus)) {
    return payload.menus;
  }
  return [];
}

function getFirstAccessiblePath(routes: any[]): string {
  if (!Array.isArray(routes)) return '';
  for (const item of routes) {
    if (!item || typeof item !== 'object') continue;
    const currentPath = typeof item.path === 'string' ? item.path.trim() : '';
    if (currentPath && currentPath !== '*' && !currentPath.includes(':') && item.component) {
      return currentPath;
    }
    const childPath = getFirstAccessiblePath(item.children || []);
    if (childPath) {
      return childPath;
    }
  }
  return '';
}

// 路由组件
export default function Router() {
  const [routes, setRoutes] = useState<any[]>([]);
  const {
    isAuthenticated,
    access_token,
    setAuthButtons,
    setRouteData,
    setOneLevelRoutes,
    oneLevelRoutes,
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const baseRoutes: RouteConfig[] = [
    {
      path: '/login',
      meta: { title: '登录' },
      component: Login,
    },
    {
      path: '/authLoading',
      meta: { title: '加载中' },
      component: AuthLoading,
    },
  ];
  const redirectRoutes: RouteConfig[] = [
    {
      path: '*',
      meta: { title: '登录' },
      component: Login,
    },
  ];

  const [staticRouteList, setStaticRouteList] = useState<MenuItem[]>([]);

  useEffect(() => {
    // 获取模块的路由信息
    const getModuleRoutes = import.meta.glob('../views/**/routes/index.ts', {
      eager: true,
      import: 'default',
    });
    Object.entries(getModuleRoutes).forEach(([, module]) => {
      setStaticRouteList((prev) => {
        return [...prev, ...(module as MenuItem[])];
      });
    });
  }, []);

  const generateRoutes = async () => {
    if (!access_token && !isAuthenticated) {
      const finalRoutes = createRoutes(baseRoutes.concat(redirectRoutes));
      setRoutes(finalRoutes);
      setAllRoutes([]);
      setOneLevelRoutes([]);
      const singleRouteList = singleRoutes(finalRoutes as TenantResDto[]);
      setOneLevelRoutes(singleRouteList);
    } else {
      searchRoute?.sort((a, b) => Number(a.id) - Number(b.id));
      const capitalPermissions = await fetchCapitalQueryPermissions();
      setAuthButtons((existing) => mergeQueryAuthorities(existing, capitalPermissions.authorityKeys));
      const dynamicRoute = filterDynamicRouter(
        multidimensionalRoute(searchRoute as TenantResDto[]),
      );

      const staticRouterRes = filterStaticRouter([
        ...staticDetailRoute,
        ...staticRouteList,
        ...nyRoutes,
      ]);

      const mergeRoutes = [
        {
          // path: "/",
          path: '',
          component: Layout,
          children: [
            ...dynamicRoute,
            ...staticRouterRes,
            // 404 路由放在最后
            {
              path: '*',
              component: PlaceholderPage,
              meta: { title: '页面不存在' },
            },
          ],
        },
        ...baseRoutes,
        {
          path: '*',
          component: PlaceholderPage,
          meta: { title: '页面不存在' },
        },
      ];
      setRouteData(dynamicRoute);
      const finalRoutes = createRoutes(mergeRoutes);
      setRoutes(finalRoutes);
      // 设置全局路由，用于面包屑等组件的匹配逻辑
      setAllRoutes(finalRoutes);
      const singleRouteList = singleRoutes(mergeRoutes as TenantResDto[]);
      setOneLevelRoutes(singleRouteList);
    }
  };

  useEffect(() => {
    generateRoutes();
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !location.pathname.startsWith('/search')) {
      return;
    }
    let active = true;
    fetchCapitalQueryPermissions().then((permissions) => {
      if (active) {
        setAuthButtons((existing) => mergeQueryAuthorities(existing, permissions.authorityKeys));
      }
    });
    return () => {
      active = false;
    };
  }, [isAuthenticated, location.pathname]);
  return useRoutes(routes);
}
